import { useEffect, useState } from 'react'
import {
  Modal,
  Stack,
  TextInput,
  Textarea,
  Select,
  NumberInput,
  Switch,
  Group,
  Button,
  Text,
  Divider,
  Paper,
  Badge,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  useAtividadePmocRegerar,
  useAtividadePmocUpdate,
  useMedicaoEsperadaCreate,
  useMedicaoEsperadaDelete,
  useMedicaoEsperadaUpdate,
  useMedicoesEsperadasList,
} from '../../hooks/usePlanosPmoc'
import {
  CRITERIOS_MEDICAO,
  EXIGENCIAS_FOTO,
  PERIODICIDADES,
  labelDe,
} from '../planos_modelo/constants'
import { extractApiError } from '../../lib/extractApiError'
import type {
  AtividadePMOC,
  AtividadePMOCUpdateInput,
  CriterioMedicao,
  ExigenciaFoto,
  MedicaoEsperadaCreateInput,
  PeriodicidadeAtividade,
} from '../../types/pmocPlanos'

interface Props {
  atividade: AtividadePMOC | null
  planoPmocId: string
  readOnly: boolean
  onClose: () => void
}

const MED_INICIAL: MedicaoEsperadaCreateInput = {
  tipo_medicao: '',
  unidade: '',
  criterio: 'apenas_registrar',
  valor_min: null,
  valor_max: null,
  valor_referencia: null,
  obrigatoria: true,
}

export default function AtividadePmocEditModal({
  atividade,
  planoPmocId,
  readOnly,
  onClose,
}: Props) {
  const update = useAtividadePmocUpdate(planoPmocId)
  const regerar = useAtividadePmocRegerar(planoPmocId)
  const meds = useMedicoesEsperadasList(atividade?.id ?? '')
  const medCreate = useMedicaoEsperadaCreate(atividade?.id ?? '')
  const medUpdate = useMedicaoEsperadaUpdate(atividade?.id ?? '')
  const medDelete = useMedicaoEsperadaDelete(atividade?.id ?? '')

  const [form, setForm] = useState<AtividadePMOCUpdateInput>({})
  const [medForm, setMedForm] = useState<MedicaoEsperadaCreateInput>(MED_INICIAL)
  const [medEditId, setMedEditId] = useState<string | null>(null)

  useEffect(() => {
    if (!atividade) return
    setForm({
      nome: atividade.nome,
      descricao: atividade.descricao,
      periodicidade: atividade.periodicidade,
      periodicidade_customizada_dias: atividade.periodicidade_customizada_dias,
      exige_foto: atividade.exige_foto,
      exige_medicao: atividade.exige_medicao,
      instrucoes_execucao: atividade.instrucoes_execucao,
      criterio_esperado: atividade.criterio_esperado,
      ordem: atividade.ordem,
      ativo: atividade.ativo,
    })
    setMedForm(MED_INICIAL)
    setMedEditId(null)
  }, [atividade?.id])

  if (!atividade) return null
  const atividadeAtiva = atividade

  async function handleSalvarAtividade() {
    try {
      await update.mutateAsync({ id: atividadeAtiva.id, data: form })
      notifications.show({ color: 'green', message: 'Atividade atualizada.' })
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha',
        message: extractApiError(err, 'Erro ao salvar.'),
      })
    }
  }

  async function handleRegerar() {
    if (
      !confirm(
        'Regerar ocorrências futuras desta atividade? Ocorrências passadas (cumpridas/em execução) não serão tocadas.',
      )
    )
      return
    try {
      const r = await regerar.mutateAsync(atividadeAtiva.id)
      notifications.show({
        color: 'green',
        title: 'Ocorrências regeradas',
        message: `Removidas: ${r.ocorrencias_removidas} · Criadas: ${r.ocorrencias_criadas}`,
      })
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha',
        message: extractApiError(err, 'Erro ao regerar.'),
      })
    }
  }

  async function handleSalvarMed() {
    if (!medForm.tipo_medicao.trim()) {
      notifications.show({ color: 'red', message: 'Tipo de medição obrigatório.' })
      return
    }
    try {
      if (medEditId) {
        await medUpdate.mutateAsync({ id: medEditId, data: medForm })
        notifications.show({ color: 'green', message: 'Medição atualizada.' })
      } else {
        await medCreate.mutateAsync(medForm)
        notifications.show({ color: 'green', message: 'Medição criada.' })
      }
      setMedForm(MED_INICIAL)
      setMedEditId(null)
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha',
        message: extractApiError(err, 'Erro ao salvar medição.'),
      })
    }
  }

  async function handleDeleteMed(id: string) {
    if (!confirm('Remover esta medição esperada?')) return
    try {
      await medDelete.mutateAsync(id)
      notifications.show({ color: 'green', message: 'Medição removida.' })
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha',
        message: extractApiError(err, 'Erro ao remover.'),
      })
    }
  }

  const mostraMin =
    medForm.criterio === 'entre' ||
    medForm.criterio === 'maior_que' ||
    medForm.criterio === 'maior_ou_igual'
  const mostraMax =
    medForm.criterio === 'entre' ||
    medForm.criterio === 'menor_que' ||
    medForm.criterio === 'menor_ou_igual'
  const mostraRef = medForm.criterio === 'igual_a'

  return (
    <Modal
      opened={!!atividade}
      onClose={onClose}
      title={`Atividade — ${atividade.nome}`}
      size="xl"
      centered
    >
      <Stack gap="md">
        {readOnly && (
          <Text size="sm" c="orange">
            PMOC não está em rascunho — edição bloqueada. Duplique o PMOC para
            criar nova versão.
          </Text>
        )}

        <TextInput
          label="Nome"
          value={form.nome ?? ''}
          onChange={(e) => setForm({ ...form, nome: e.currentTarget.value })}
          disabled={readOnly}
        />
        <Textarea
          label="Descrição"
          autosize
          minRows={1}
          value={form.descricao ?? ''}
          onChange={(e) => setForm({ ...form, descricao: e.currentTarget.value })}
          disabled={readOnly}
        />
        <Group grow>
          <Select
            label="Periodicidade"
            data={PERIODICIDADES}
            value={form.periodicidade}
            onChange={(v) =>
              v &&
              setForm({ ...form, periodicidade: v as PeriodicidadeAtividade })
            }
            disabled={readOnly}
          />
          {form.periodicidade === 'customizada_dias' && (
            <NumberInput
              label="Dias"
              min={1}
              value={form.periodicidade_customizada_dias ?? ''}
              onChange={(v) =>
                setForm({
                  ...form,
                  periodicidade_customizada_dias:
                    typeof v === 'number' ? v : null,
                })
              }
              disabled={readOnly}
            />
          )}
          <NumberInput
            label="Ordem"
            min={0}
            value={form.ordem ?? 0}
            onChange={(v) =>
              setForm({ ...form, ordem: typeof v === 'number' ? v : 0 })
            }
            disabled={readOnly}
          />
        </Group>
        <Textarea
          label="Instruções de execução"
          autosize
          minRows={1}
          value={form.instrucoes_execucao ?? ''}
          onChange={(e) =>
            setForm({ ...form, instrucoes_execucao: e.currentTarget.value })
          }
          disabled={readOnly}
        />
        <TextInput
          label="Critério esperado"
          value={form.criterio_esperado ?? ''}
          onChange={(e) =>
            setForm({ ...form, criterio_esperado: e.currentTarget.value })
          }
          disabled={readOnly}
        />
        <Group grow align="flex-end">
          <Select
            label="Exige foto"
            data={EXIGENCIAS_FOTO}
            value={form.exige_foto ?? 'nunca'}
            onChange={(v) =>
              v && setForm({ ...form, exige_foto: v as ExigenciaFoto })
            }
            disabled={readOnly}
            data-testid="atividade-pmoc-exige-foto"
          />
          <Switch
            label="Exige medição"
            checked={form.exige_medicao ?? false}
            onChange={(e) =>
              setForm({ ...form, exige_medicao: e.currentTarget.checked })
            }
            disabled={readOnly}
          />
          <Switch
            label="Ativa"
            checked={form.ativo ?? true}
            onChange={(e) =>
              setForm({ ...form, ativo: e.currentTarget.checked })
            }
            disabled={readOnly}
          />
        </Group>

        {!readOnly && (
          <Group justify="space-between">
            <Button
              variant="default"
              onClick={handleRegerar}
              loading={regerar.isPending}
              title="Apaga ocorrências pendentes futuras e regenera a partir de hoje"
            >
              ⟳ Regerar ocorrências futuras
            </Button>
            <Button
              onClick={handleSalvarAtividade}
              loading={update.isPending}
              data-testid="atividade-pmoc-salvar"
            >
              Salvar atividade
            </Button>
          </Group>
        )}

        <Divider label="Medições esperadas" labelPosition="left" />

        {meds.isLoading ? (
          <Text c="dimmed">Carregando…</Text>
        ) : (meds.data?.results.length ?? 0) === 0 ? (
          <Text c="dimmed" size="sm">
            Nenhuma medição esperada cadastrada para esta atividade.
          </Text>
        ) : (
          <Stack gap="xs">
            {meds.data?.results.map((m) => (
              <Paper key={m.id} p="xs" withBorder>
                <Group justify="space-between" wrap="nowrap">
                  <div style={{ flex: 1 }}>
                    <Group gap="xs">
                      <Text fw={600} size="sm">
                        {m.tipo_medicao}
                      </Text>
                      {m.unidade && (
                        <Badge size="sm" variant="light">
                          {m.unidade}
                        </Badge>
                      )}
                      <Badge size="sm" variant="light" color="cyan">
                        {labelDe(m.criterio, CRITERIOS_MEDICAO)}
                      </Badge>
                      {!m.obrigatoria && (
                        <Badge size="sm" color="gray">
                          opcional
                        </Badge>
                      )}
                    </Group>
                    <Text size="xs" c="dimmed">
                      {m.criterio === 'entre' &&
                        `Faixa aceita: ${m.valor_min ?? '?'} a ${m.valor_max ?? '?'}`}
                      {m.criterio === 'maior_que' &&
                        `Mínimo aceito: ${m.valor_min ?? '?'}`}
                      {m.criterio === 'maior_ou_igual' &&
                        `Mínimo aceito (inclusive): ${m.valor_min ?? '?'}`}
                      {m.criterio === 'menor_que' &&
                        `Máximo aceito: ${m.valor_max ?? '?'}`}
                      {m.criterio === 'menor_ou_igual' &&
                        `Máximo aceito (inclusive): ${m.valor_max ?? '?'}`}
                      {m.criterio === 'igual_a' &&
                        `Referência: ${m.valor_referencia ?? '?'}`}
                      {m.criterio === 'apenas_registrar' && '(somente registro)'}
                    </Text>
                  </div>
                  {!readOnly && (
                    <Group gap="xs">
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => {
                          setMedEditId(m.id)
                          setMedForm({
                            tipo_medicao: m.tipo_medicao,
                            unidade: m.unidade,
                            criterio: m.criterio,
                            valor_min: m.valor_min,
                            valor_max: m.valor_max,
                            valor_referencia: m.valor_referencia,
                            obrigatoria: m.obrigatoria,
                          })
                        }}
                      >
                        Editar
                      </Button>
                      <Button
                        size="xs"
                        color="red"
                        variant="subtle"
                        onClick={() => handleDeleteMed(m.id)}
                      >
                        Remover
                      </Button>
                    </Group>
                  )}
                </Group>
              </Paper>
            ))}
          </Stack>
        )}

        {!readOnly && (
          <Paper p="sm" withBorder>
            <Text fw={600} size="sm" mb="xs">
              {medEditId ? 'Editar medição' : 'Nova medição esperada'}
            </Text>
            <Stack gap="xs">
              <Group grow>
                <TextInput
                  label="Tipo de medição"
                  placeholder="Ex.: CO₂, temperatura, ΔP"
                  required
                  value={medForm.tipo_medicao}
                  onChange={(e) =>
                    setMedForm({ ...medForm, tipo_medicao: e.currentTarget.value })
                  }
                  data-testid="medicao-tipo"
                />
                <TextInput
                  label="Unidade"
                  placeholder="ppm, °C, Pa"
                  value={medForm.unidade ?? ''}
                  onChange={(e) =>
                    setMedForm({ ...medForm, unidade: e.currentTarget.value })
                  }
                />
                <Select
                  label="Critério"
                  data={CRITERIOS_MEDICAO}
                  value={medForm.criterio}
                  onChange={(v) =>
                    v && setMedForm({ ...medForm, criterio: v as CriterioMedicao })
                  }
                  data-testid="medicao-criterio"
                />
              </Group>
              <Group grow>
                {mostraMin && (
                  <TextInput
                    label="Valor mínimo"
                    value={medForm.valor_min ?? ''}
                    onChange={(e) =>
                      setMedForm({
                        ...medForm,
                        valor_min: e.currentTarget.value || null,
                      })
                    }
                  />
                )}
                {mostraMax && (
                  <TextInput
                    label="Valor máximo"
                    value={medForm.valor_max ?? ''}
                    onChange={(e) =>
                      setMedForm({
                        ...medForm,
                        valor_max: e.currentTarget.value || null,
                      })
                    }
                  />
                )}
                {mostraRef && (
                  <TextInput
                    label="Valor de referência"
                    value={medForm.valor_referencia ?? ''}
                    onChange={(e) =>
                      setMedForm({
                        ...medForm,
                        valor_referencia: e.currentTarget.value || null,
                      })
                    }
                  />
                )}
              </Group>
              <Switch
                label="Obrigatória"
                checked={medForm.obrigatoria ?? true}
                onChange={(e) =>
                  setMedForm({ ...medForm, obrigatoria: e.currentTarget.checked })
                }
              />
              <Group justify="flex-end">
                {medEditId && (
                  <Button
                    variant="default"
                    onClick={() => {
                      setMedForm(MED_INICIAL)
                      setMedEditId(null)
                    }}
                  >
                    Cancelar edição
                  </Button>
                )}
                <Button
                  onClick={handleSalvarMed}
                  loading={medCreate.isPending || medUpdate.isPending}
                  data-testid="medicao-submit"
                >
                  {medEditId ? 'Salvar medição' : '+ Adicionar medição'}
                </Button>
              </Group>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Modal>
  )
}
